import React from 'react';

import { Button, CircularProgress, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';

import CenteredGrid from 'components/CenteredGrid/CenteredGrid';

const useStyles = makeStyles(theme => ({
  heading: {
    marginBottom: theme.spacing(2),
  },
  error: {
    minHeight: theme.spacing(3),
    fontSize: '0.875rem',
  },
  button: {
    overflow: 'hidden',
    position: 'relative',
    zIndex: 1,
    '&.MuiButtonBase-root.Mui-disabled': {
      color: ({ hasCountdown }) => hasCountdown && theme.palette.text.primary,
    },
  },
}));

export const formatRemainingTime = remainingSeconds => {
  if (!Number.isFinite(remainingSeconds) || remainingSeconds <= 0) return null;

  const totalSeconds = Math.floor(remainingSeconds);
  if (totalSeconds <= 0) return null;

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const SimpleFormLayout = ({
  onSubmit,
  submitLabel,
  title,
  children,
  disabled,
  generalError,
  onChange,
  buttonVariant,
  remainingSeconds,
  isForm,
  FORM_ID,
  sending,
  additionalButtons,
}) => {
  const countdownLabel = formatRemainingTime(remainingSeconds);
  const styles = useStyles({ hasCountdown: Boolean(countdownLabel) });

  const getLabel = () => {
    if (sending) return <CircularProgress color='primary' size={22} />;
    if (countdownLabel) return countdownLabel;
    return submitLabel;
  };

  return (
    <CenteredGrid component={isForm ? 'form' : 'div'} gridGap={1.5} onChange={onChange} onSubmit={onSubmit} width='xs'>
      {title && (
        <Typography className={styles.heading} component='h2' variant='h2'>
          {title}
        </Typography>
      )}
      {children}
      <Typography align='center' className={styles.error} color='error' data-cy='simple_form_error'>
        {generalError}
      </Typography>
      {additionalButtons && additionalButtons()}
      <Button
        className={styles.button}
        color='primary'
        data-cy='simple_form_submit'
        data-testid='simple-form'
        disabled={disabled || Boolean(countdownLabel) || sending}
        form={FORM_ID}
        type='submit'
        variant={buttonVariant}
      >
        {getLabel()}
      </Button>
    </CenteredGrid>
  );
};

SimpleFormLayout.propTypes = {
  onSubmit: PropTypes.func,
  onChange: PropTypes.func,
  submitLabel: PropTypes.string,
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
  generalError: PropTypes.string,
  buttonVariant: PropTypes.string,
  remainingSeconds: PropTypes.number,
  isForm: PropTypes.bool,
  FORM_ID: PropTypes.string,
  sending: PropTypes.bool,
  additionalButtons: PropTypes.func,
};

SimpleFormLayout.defaultProps = {
  submitLabel: 'Ok',
  title: null,
  generalError: null,
  onChange: () => {},
  onSubmit: () => {},
  disabled: false,
  buttonVariant: 'contained',
  remainingSeconds: null,
  isForm: true,
  FORM_ID: null,
  sending: false,
  additionalButtons: null,
};

export default SimpleFormLayout;
